import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { BidMasterLogo } from '../components/BidMasterLogo';
import { MainAppDrawer } from '../components/MainAppDrawer';
import { logout } from '../store/slices/authSlice';
import { persistor } from '../store';
import { authService } from '../services/authService';
import { useQueryClient } from '@tanstack/react-query';
import {
  MenuHamburgerIcon,
  TabCreateIcon,
  TabHistoryIcon,
  TabHomeIcon,
  TabProfileIcon,
  TabSubscriptionIcon,
} from '../components/BottomTabBarIcons';

const tabClass =
  'flex flex-col items-center justify-center gap-1 py-2.5 px-2 min-w-0 flex-1 text-xs font-semibold text-[#6b7280]';
const activeTab = 'text-[#2563eb]';

export function MainLayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((s: RootState) => s.auth.user);
  const [menuOpen, setMenuOpen] = useState(false);

  const signOut = async () => {
    await authService.signOut();
    dispatch(logout());
    await persistor.purge();
    queryClient.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 pb-24">
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 px-4 h-14 flex items-center justify-between">
        <button
          type="button"
          className="flex h-10 w-10 shrink-0 items-center justify-center text-slate-600"
          onClick={() => setMenuOpen(true)}
          aria-label="Menu"
        >
          <MenuHamburgerIcon />
        </button>
        <BidMasterLogo href={false} />
        <span className="w-10 shrink-0" aria-hidden />
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-4">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex justify-around items-stretch z-40"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <NavLink to="/home" end className={({ isActive }) => `${tabClass} ${isActive ? activeTab : ''}`}>
          {({ isActive }) => (
            <>
              <TabHomeIcon active={isActive} />
              <span>Home</span>
            </>
          )}
        </NavLink>
        <NavLink to="/history" className={({ isActive }) => `${tabClass} ${isActive ? activeTab : ''}`}>
          {({ isActive }) => (
            <>
              <TabHistoryIcon active={isActive} />
              <span>History</span>
            </>
          )}
        </NavLink>
        <NavLink to="/create" className={({ isActive }) => `${tabClass} ${isActive ? activeTab : ''}`}>
          {({ isActive }) => (
            <>
              <TabCreateIcon active={isActive} />
              <span className="text-center leading-tight">Create Bid</span>
            </>
          )}
        </NavLink>
        <NavLink to="/subscription" className={({ isActive }) => `${tabClass} ${isActive ? activeTab : ''}`}>
          {({ isActive }) => (
            <>
              <TabSubscriptionIcon active={isActive} />
              <span>Subscription</span>
            </>
          )}
        </NavLink>
        <NavLink to="/profile" className={({ isActive }) => `${tabClass} ${isActive ? activeTab : ''}`}>
          {({ isActive }) => (
            <>
              <TabProfileIcon active={isActive} />
              <span>Profile</span>
            </>
          )}
        </NavLink>
      </nav>

      <MainAppDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        onOpenSubscription={() => navigate('/subscription')}
        onSignOut={signOut}
      />
    </div>
  );
}
